import React, { useRef, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { AutocompleteClassKey } from '@material-ui/lab';
import { TextField } from '@michelin/acid-components';
import { arrayable } from 'utils/arrayable';
import { ClassNameMap } from '@material-ui/styles';

const useStyles = makeStyles(() =>
  createStyles({
    paper: {
      fontSize: 11,
    },
    option: {
      minHeight: 'auto',
      width: '100%',
      fontSize: 11,
      padding: 0,
      '&[aria-selected="true"]': {
        backgroundColor: 'rgba(0,0,0,0.4)',
      },
      '&[data-focus="true"]': {
        backgroundColor: 'rgba(0,0,0,0.2)',
      },
    },
  }),
);

export interface SearchSelectorOption<T> {
  label?: string | number;
  value: string | number;
  data?: T;
}

interface BasicProps<T> {
  label: string;
  readonly?: boolean;
  loading?: boolean;
  options: SearchSelectorOption<T>[];
  onSearch?: (value: any) => void;
  value?: any;
  multiple?: boolean;
  renderOption?: React.SFC<SearchSelectorOption<T>>;
  renderSelection?: React.SFC<SearchSelectorOption<T>>;
  getOptionSelected?: (option: SearchSelectorOption<T>, value: SearchSelectorOption<T>) => boolean;
}

interface AutocompleteSelectProps<T> extends BasicProps<T> {
  onChange: (event: React.ChangeEvent<any>, value: SearchSelectorOption<T>) => void;
  onOpen?: (event: React.ChangeEvent<any>) => void;
  classes?: Partial<ClassNameMap<AutocompleteClassKey>>;
}

interface OwnPropsSearch<T> extends BasicProps<T> {
  onChange: (event: React.ChangeEvent<any>, value: string[]) => void;
}

export function ChipOption<T>({ label, value }: SearchSelectorOption<T>) {
  const chipOptionId = label ? `${value}_${label}` : `${value}`;

  return <div id={chipOptionId}>{label}</div>;
}

interface CustomAutocompleteSelector<T> extends AutocompleteSelectProps<T> {
  placeholder?: string;
  style?: React.CSSProperties;
  freeSolo?: boolean;
  noOptionsText?: string;
  filterOptions?: any;
  onInputChange?: any;
  onBlur?: any;
  popupIcon?: any;
  id?: string;
}

export function AutocompleteSelect<T>({
  getOptionSelected,
  filterOptions,
  noOptionsText = 'No options',
  label,
  options,
  onChange,
  onSearch,
  onOpen,
  value,
  multiple = false,
  readonly,
  loading = false,
  placeholder,
  style,
  onBlur,
  onInputChange,
  popupIcon,
  id,
  // eslint-disable-next-line no-unused-vars
  renderOption: RenderOptionCustom = ChipOption,
  classes: classesProp,
}: CustomAutocompleteSelector<T>) {
  const classes = useStyles();
  return (
    <Autocomplete
      id={id}
      popupIcon={popupIcon}
      onBlur={onBlur}
      onInputChange={onInputChange}
      getOptionSelected={getOptionSelected}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      loading={loading}
      onOpen={onOpen}
      multiple={multiple as any}
      value={value}
      disabled={readonly}
      onChange={onChange as any}
      options={options}
      classes={{
        ...classesProp,
        paper: classes.paper,
        option: classes.option,
      }}
      getOptionLabel={(option) => {
        return `${arrayable(option)[0].label ?? ''}`;
      }}
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField label={label} placeholder={placeholder} onChange={onSearch} {...params} />
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      renderOption={(option) => <RenderOptionCustom {...option} />}
      renderTags={(tagValue) => tagValue ?? ''}
      style={style}
    />
  );
}

export function SearchSelector<T>({
  label,
  options,
  onChange,
  value,
  multiple = false,
  renderOption: RenderOptionCustom = ChipOption,
  renderSelection: RenderSelection = MenuItem,
}: OwnPropsSearch<T>) {
  const ref = useRef({} as any);
  const [open, setOpen] = useState(false);
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onChange(event, event.target.value as string[]);
    setOpen(false);
  };
  // const RenderOptionCustom: React.SFC<SearchSelectorOption<T>> = (renderOption || ChipOption) as any;
  return (
    <>
      <FormControl style={{ width: '100%' }} ref={ref}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple={multiple}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={value || []}
          onChange={handleChange}
          renderValue={(selected) => (
            <div>
              {options
                .filter((o) => (multiple ? (selected as string[]).includes(String(o.value)) : o.value === selected))
                .map(({ value, label, data }) => (
                  <RenderOptionCustom key={value} value={value} label={label} data={data} />
                ))}
            </div>
          )}
        >
          {options.map(({ label, value }) => (
            <RenderSelection key={value} value={value}>
              {label}
            </RenderSelection>
          ))}
        </Select>
      </FormControl>
    </>
  );
}
